
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-gray-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <img 
              src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
              alt="GAMBGO Logo"
              className="w-8 h-8"
            />
            <span className="text-lg font-bold text-gray-900">GAMBGO</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Terms & Conditions
          </h1>
          <p className="text-lg text-gray-600">
            Last updated: June 5, 2025
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          <div className="bg-green-50 rounded-lg p-6 mb-8">
            <p className="text-green-900 font-medium">
              Welcome to GAMBGO! These Terms and Conditions govern your use of our food and grocery delivery platform. By using our services, you agree to comply with and be bound by these terms.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using the GAMBGO platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions and our Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
            <p className="text-gray-700 mb-4">
              GAMBGO is a digital platform that connects customers with local restaurants, grocery stores, and other vendors for food and product delivery services in The Gambia.
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>We facilitate orders between customers and merchants</li>
              <li>We coordinate delivery through our network of drivers</li>
              <li>We process payments on behalf of merchants</li>
              <li>We provide customer support services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Registration</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li>You must be at least 18 years old to create an account</li>
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining account security</li>
              <li>One account per person is permitted</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Responsibilities</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Keep your login credentials confidential</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Ensure your contact information is current</li>
              <li>Use the platform only for lawful purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Orders and Payments</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Order Process</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li>Orders are subject to merchant acceptance</li>
              <li>Prices may vary from those listed in-store</li>
              <li>We reserve the right to cancel orders for any reason</li>
              <li>Order modifications may not be possible after confirmation</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Payment Terms</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Payment is due at the time of order placement</li>
              <li>We accept cash on delivery and mobile money payments</li>
              <li>Delivery fees and taxes are clearly displayed before checkout</li>
              <li>Refunds are processed according to our refund policy</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Delivery Services</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Delivery times are estimates and not guaranteed</li>
              <li>You must be available to receive deliveries at the specified address</li>
              <li>Additional charges may apply for failed delivery attempts</li>
              <li>We are not liable for delays caused by weather, traffic, or other circumstances beyond our control</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cancellations and Refunds</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Cancellation Policy</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li>Orders can be cancelled within 5 minutes of placement</li>
              <li>Cancellations after merchant preparation may incur charges</li>
              <li>We reserve the right to cancel orders due to merchant unavailability</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Refund Policy</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Refunds are processed for cancelled orders and quality issues</li>
              <li>Refund processing time is 3-5 business days</li>
              <li>Delivery fees may be non-refundable in certain cases</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Prohibited Uses</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Use the platform for illegal activities</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Impersonate another person or entity</li>
              <li>Interfere with the platform's operation</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated tools to access the platform</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              GAMBGO acts as an intermediary between customers and merchants. We are not responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Food quality, safety, or preparation</li>
              <li>Merchant availability or service quality</li>
              <li>Allergic reactions or dietary restrictions</li>
              <li>Damages arising from delivery delays</li>
              <li>Loss or damage to property during delivery</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              All content on the GAMBGO platform, including logos, trademarks, text, and images, is owned by GAMBGO or our partners and is protected by intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Privacy</h2>
            <p className="text-gray-700">
              Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Modifications</h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting. Your continued use of the platform constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-700">
              These Terms and Conditions are governed by the laws of The Republic of The Gambia. Any disputes will be resolved through the courts of The Gambia.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Email:</strong> gambgomobile@gmail.com</li>
                <li><strong>Phone:</strong> +2202554992</li>
                <li><strong>Address:</strong> GAMBGO Headquarters, Sukuta, The Gambia</li>
              </ul>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
              alt="GAMBGO Logo"
              className="w-8 h-8"
            />
            <span className="text-xl font-bold">GAMBGO</span>
          </div>
          <p className="text-gray-400">&copy; 2026 GAMBGO. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditionsPage;
